import { api } from './client'
import type { ApiEnvelope, PaginatedResponse, Robot } from '@/types/api'

interface SpringPage<T> {
    content: T[]
    totalElements: number
    totalPages: number
    number: number
    size: number
}

function adaptPage<T>(raw: SpringPage<T> | PaginatedResponse<T>): PaginatedResponse<T> {
    if ('items' in raw) return raw
    return {
        items: raw.content,
        total: raw.totalElements,
        page: raw.number + 1,
        limit: raw.size,
        totalPages: raw.totalPages,
    }
}

export interface RobotQueryParams {
    robotId?: string
    unitId?: string
    status?: number
    startDate?: string
    endDate?: string
    deleted?: number
}

interface RobotApiPosition {
    id?: unknown
    name?: unknown
    x?: unknown
    y?: unknown
}

interface RobotApiResponse {
    id?: unknown
    code?: unknown
    robotId?: unknown
    name?: unknown
    agentName?: unknown
    robotName?: unknown
    unitId?: unknown
    unitName?: unknown
    workTime?: unknown
    startTime?: unknown
    endTime?: unknown
    minEnergy?: unknown
    minimumEnergy?: unknown
    energyUnit?: unknown
    timeoutUnit?: unknown
    timeout?: unknown
    voice?: unknown
    voiceId?: unknown
    promptDescription?: unknown
    task?: unknown
    compliance?: unknown
    guardrails?: unknown
    greeting?: unknown
    isActive?: unknown
    status?: unknown
    createdAt?: unknown
    updatedAt?: unknown
    chargingStation?: unknown
    positions?: unknown
    systemPrompt?: unknown;
    unitResponse?: {
        id?: unknown;
        name?: unknown;
        status?: unknown;
    };
    lowBatteryThreshold?: unknown;
    startupMode: number;
    activate?: number;
    voicePrompt?: unknown;
    robotWorkingDays?: unknown[];
    knowledgeBases?: unknown[];
    diagramInfo?: unknown;
    state?: string;
}

export interface RobotVoiceOption {
    value: string
    label: string
    rawId?: string
    voiceName?: string
    description?: string
    userId?: string
    langCodes?: string[]
    langCode?: string
    viVoiceId?: string
    enVoiceId?: string
}

export interface RobotUnitOption {
    value: string
    label: string
}

export interface RobotWorkingDayPayload {
    createdList: Array<{ dayOfWeek: number[]; startTime: string; endTime: string }>
    updatedList: Array<{ id: string; dayOfWeek: number[]; startTime: string; endTime: string; status: number }>
    deletedIdList: string[]
}

export interface ActivateRobotPayload {
    name: string
    unitId: string
}

function asString(value: unknown): string | undefined {
    return typeof value === 'string' ? value : undefined
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null
}

function asNumber(value: unknown): number | undefined {
    return typeof value === 'number' && !Number.isNaN(value) ? value : undefined
}

function normalizeRobotPositions(payload: unknown): Robot['positions'] {
    if (!Array.isArray(payload)) return undefined

    const positions = payload
        .map((item) => {
            if (!isRecord(item)) return null

            const raw = item as RobotApiPosition
            const id = asString(raw.id)
            const name = asString(raw.name)
            const x = asNumber(raw.x)
            const y = asNumber(raw.y)

            if (!id || !name || x === undefined || y === undefined) return null

            return { id, name, x, y }
        })
        .filter((item): item is NonNullable<Robot['positions']>[number] => item !== null)

    return positions.length > 0 ? positions : undefined
}

function normalizeRobotWorkTime(raw: RobotApiResponse): Robot['workTime'] {
    if (
        isRecord(raw.workTime) &&
        typeof raw.workTime.start === 'string' &&
        typeof raw.workTime.end === 'string'
    ) {
        return { start: raw.workTime.start, end: raw.workTime.end }
    }

    const start = asString(raw.startTime)
    const end = asString(raw.endTime)

    if (start && end) {
        return { start, end }
    }

    return asString(raw.workTime)
}

function normalizeVoicePrompt(payload: unknown): Robot['voicePrompt'] {
    if (!isRecord(payload)) return undefined

    const greeting = asString(payload.greeting)
    const idlePrompt = asString(payload.idlePrompt) ?? asString(payload.prompt60s)
    const idleGoodbye = asString(payload.idleGoodbye) ?? asString(payload.prompt120s)
    const preCallPrompt = asString(payload.preCallPrompt)
    const postCallPrompt = asString(payload.postCallPrompt) ?? asString(payload.endPrompt)

    if (!greeting && !idlePrompt && !idleGoodbye && !preCallPrompt && !postCallPrompt) {
        return undefined
    }

    return {
        greeting,
        idlePrompt,
        idleGoodbye,
        preCallPrompt,
        postCallPrompt,
    }
}

function buildVoicePromptPayload(input: {
    greeting?: unknown
    prompt60s?: unknown
    prompt120s?: unknown
    preCallPrompt?: unknown
    endPrompt?: unknown
}): Robot['voicePrompt'] | undefined {
    const greeting = asString(input.greeting)
    const idlePrompt = asString(input.prompt60s)
    const idleGoodbye = asString(input.prompt120s)
    const preCallPrompt = asString(input.preCallPrompt)
    const postCallPrompt = asString(input.endPrompt)

    if (!greeting && !idlePrompt && !idleGoodbye && !preCallPrompt && !postCallPrompt) {
        return undefined
    }

    return {
        greeting,
        idlePrompt,
        idleGoodbye,
        preCallPrompt,
        postCallPrompt,
    }
}

function normalizeRobot(raw: RobotApiResponse): Robot {
    const id = asString(raw.id) ?? asString(raw.robotId) ?? ''
    const code = asString(raw.code) ?? asString(raw.robotId) ?? id
    let resolvedVoice = '';
    if (raw.voice && typeof raw.voice === 'object') {
        resolvedVoice = asString((raw.voice as any).collectionId) || asString((raw.voice as any).id) || '';
    } else {
        resolvedVoice = asString(raw.voiceId) || asString(raw.voice) || '';
    }

    const diagramSource = raw.diagramInfo
    const diagramRaw = isRecord(diagramSource) ? diagramSource : null
    const diagramFileInfoRaw = diagramRaw && isRecord(diagramRaw.fileInfo)
        ? diagramRaw.fileInfo
        : null

    const robotWorkingDays = Array.isArray(raw.robotWorkingDays)
        ? raw.robotWorkingDays.map((wd: any) => {
            let dayOfWeekValues: number[] = [];
            if (typeof wd.dayOfWeek === 'number') {
                dayOfWeekValues = [wd.dayOfWeek];
            } else if (Array.isArray(wd.dayOfWeek) && wd.dayOfWeek.length > 0) {
                dayOfWeekValues = wd.dayOfWeek.map((v: any) =>
                    typeof v === 'object' && v !== null && v.value !== undefined
                        ? Number(v.value)
                        : Number(v)
                ).filter((v: number) => Number.isFinite(v));
            }

            const normalizedDayOfWeek = dayOfWeekValues.length <= 1
                ? (dayOfWeekValues[0] ?? 0)
                : dayOfWeekValues;

            return {
                id: String(wd.id || ''),
                robotId: String(wd.robotId || ''),
                dayOfWeek: normalizedDayOfWeek,
                dayOfWeekName: String(wd.dayOfWeekName || ''),
                startTime: String(wd.startTime || ''),
                endTime: String(wd.endTime || ''),
                status: Number(wd.status) || 1
            }
        })
        : undefined;

    return {
        id,
        code,
        agentName: asString(raw.agentName) ?? '',
        name: asString(raw.name) ?? asString(raw.robotName) ?? '-',
        workTime: normalizeRobotWorkTime(raw),
        minEnergy: asNumber(raw.minEnergy) ?? asNumber(raw.minimumEnergy) ?? 10,
        energyUnit: asString(raw.energyUnit) ?? '%',
        timeoutUnit: asString(raw.timeoutUnit) ?? 's',
        timeout: asNumber(raw.timeout) ?? 10,
        voice: resolvedVoice,
        promptDescription: asString(raw.promptDescription) ?? asString(raw.task) ?? '',
        guardrails: asString(raw.guardrails),
        createdAt: asString(raw.createdAt) ?? '',
        updatedAt: asString(raw.updatedAt) ?? asString(raw.createdAt) ?? '',
        diagramInfo: diagramRaw
            ? {
                id: asString(diagramRaw.id) ?? asString(diagramFileInfoRaw?.id) ?? '',
                name: asString(diagramRaw.name) ?? asString(diagramFileInfoRaw?.name) ?? '',
                originFileName: asString(diagramRaw.originFileName) ?? asString(diagramFileInfoRaw?.originFileName) ?? '',
                contentType: asString(diagramRaw.contentType) ?? asString(diagramFileInfoRaw?.contentType) ?? '',
                filePath: asString(diagramRaw.filePath) ?? asString(diagramFileInfoRaw?.filePath) ?? '',
                fileUrl: asString(diagramRaw.fileUrl) ?? asString(diagramFileInfoRaw?.fileUrl) ?? '',
                width: asNumber(diagramRaw.width) ?? asNumber(diagramFileInfoRaw?.width),
                height: asNumber(diagramRaw.height) ?? asNumber(diagramFileInfoRaw?.height),
                expiresIn:
                    asNumber(diagramRaw.expiresIn) ?? asNumber(diagramFileInfoRaw?.expiresIn) ?? 0,
            }
            : undefined,
        chargingStation: asString(raw.chargingStation),
        positions: normalizeRobotPositions(raw.positions),
        systemPrompt: asString(raw.systemPrompt),
        unitResponse: raw.unitResponse && typeof raw.unitResponse === 'object' ? {
            id: asString(raw.unitResponse.id) ?? '',
            name: asString(raw.unitResponse.name) ?? '',
            status: asNumber(raw.unitResponse.status) ?? 0,
        } : undefined,
        lowBatteryThreshold: asNumber(raw.lowBatteryThreshold),
        startupMode: raw.startupMode,
        activate: asNumber(raw.activate),
        voicePrompt: normalizeVoicePrompt(raw.voicePrompt),
        status: asNumber(raw.status),
        startTime: asString(raw.startTime),
        endTime: asString(raw.endTime),
        mapFileUrl: asString((raw as any).mapFileUrl),
        robotWorkingDays,
        knowledgeBases: Array.isArray((raw as any).knowledgeBases)
            ? (raw as any).knowledgeBases
                .map((item: any) => {
                    if (typeof item === 'string') {
                        return { id: item, name: '', createdAt: '' }
                    }
                    if (item && typeof item === 'object') {
                        const kbId = asString(item.id) ?? asString(item.kbId)
                        const name = asString(item.name) ?? ''
                        const createdAt = asString(item.createdAt) ?? ''
                        return kbId ? { id: kbId, name, createdAt } : null
                    }
                    return null
                })
                .filter((x: any): x is { id: string; name: string; createdAt: string } => x !== null)
            : undefined,
        state: asString(raw.state) ?? '',
    }
}

function adaptRobotPage(raw: SpringPage<RobotApiResponse> | PaginatedResponse<RobotApiResponse>): PaginatedResponse<Robot> {
    const page = adaptPage(raw)
    return {
        ...page,
        items: page.items.map((item) => normalizeRobot(item)),
    }
}

function normalizeVoiceOptions(payload: unknown): RobotVoiceOption[] {
    if (!payload) return []

    if (Array.isArray(payload)) {
        const options: RobotVoiceOption[] = [];

        for (const item of payload) {
            if (typeof item === 'string') {
                options.push({ value: item, label: item });
                continue;
            }

            if (!isRecord(item)) continue;

            const raw = item as Record<string, any>;
            const rawId = asString(raw.id);
            const voiceName = asString(raw.name);
            const value = rawId || asString(raw.value) || asString(raw.code);
            const label = voiceName || asString(raw.label) || value;

            if (!value || !label) continue;

            const option: RobotVoiceOption = {
                value,
                label,
                langCode: 'vi',
            };
            if (rawId) option.rawId = rawId;
            if (voiceName) option.voiceName = voiceName;
            if (asString(raw.description)) option.description = asString(raw.description);
            if (asString(raw.user_id)) option.userId = asString(raw.user_id);
            options.push(option);
        }

        const seen = new Set<string>();
        return options.filter(opt => {
            if (seen.has(opt.value)) return false;
            seen.add(opt.value);
            return true;
        });
    }

    if (isRecord(payload)) {
        const nestedArray = payload.content ?? payload.items ?? payload.voices ?? payload.data;
        if (Array.isArray(nestedArray)) {
            return normalizeVoiceOptions(nestedArray);
        }
    }

    return [];
}

export async function fetchRobots(
    page: number,
    limit: number,
    params: RobotQueryParams = {},
    signal?: AbortSignal
): Promise<PaginatedResponse<Robot>> {
    const { robotId, unitId, status, startDate, endDate, deleted } = params
    try {
        const response = await api.get<ApiEnvelope<SpringPage<RobotApiResponse>>>(
            '/v1/robot/config/search',
            {
                params: {
                    page: page,
                    size: limit,
                    robotId,
                    unitId,
                    status,
                    startDate,
                    endDate,
                    deleted,
                },
                signal
            },
        )
        return adaptRobotPage(response.data.responseData)
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi lấy danh sách robot.';
        throw new Error(message);
    }
}

export async function fetchRobotVoices(signal?: AbortSignal): Promise<RobotVoiceOption[]> {
    try {
        const response = await api.get<unknown>('/v1/robot/config/get-voices', { signal })
        const payload = (response.data as { responseData?: unknown }).responseData ?? response.data
        return normalizeVoiceOptions(payload)
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi lấy danh sách giọng nói.';
        throw new Error(message);
    }
}

export async function updateRobotWorkingDays(robotId: string, payload: RobotWorkingDayPayload) {
    const response = await api.put(`/v1/robot/config/update/working-day/${robotId}`, payload)
    return response.data
}

/**
 * Get robot by ID
 */
export async function getRobotById(id: string, signal?: AbortSignal): Promise<Robot> {
    try {
        const response = await api.get<ApiEnvelope<RobotApiResponse>>(`/v1/robot/config/detail/${id}`, { signal })
        return normalizeRobot(response.data.responseData)
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi lấy thông tin robot.';
        throw new Error(message);
    }
}

/**
 * Create new robot
 */
export async function createRobot(
    data: Omit<Robot, 'id' | 'code' | 'createdAt' | 'updatedAt'> & Record<string, any>,
): Promise<Robot> {
    const payload: Record<string, any> = { ...data };
    if (payload.voice) {
        const voiceStr = typeof payload.voice === 'object' ? payload.voice.id : payload.voice;
        payload.voice = voiceStr;
        payload.voiceId = voiceStr;
    }
    payload.voicePrompt = buildVoicePromptPayload({
        greeting: data.greeting,
        prompt60s: (data as any).prompt60s,
        prompt120s: (data as any).prompt120s,
        preCallPrompt: (data as any).preCallPrompt,
        endPrompt: (data as any).endPrompt,
    });

    const response = await api.post<ApiEnvelope<RobotApiResponse>>('/v1/robot/config', payload)
    return normalizeRobot(response.data.responseData)
}

/**
 * Update robot
 */
export async function updateRobot(
    id: string,
    data: any,
): Promise<any> {
    const payload: Record<string, any> = {
        status: data.status,
        name: data.name,
        unitId: data.unitId,
        diagramId: data.diagramId,
    };

    const response = await api.put(
        `/v1/robot/config/update/${id}`,
        payload,
    );
    return response.data;
}

/**
 * Update config robot
 */
export async function updateConfigRobot(
    id: string,
    data: any,
): Promise<any> {
    const voiceStr = data.voice && typeof data.voice === 'object' ? (data.voice.collectionId || data.voice.id) : data.voice;
    const newkbIds = Array.isArray(data.knowledgeBases) ? data.knowledgeBases.map((kb: any) => kb.value || kb) : [];

    const payload: Record<string, any> = {
        startTime: data.startTime,
        endTime: data.endTime,
        lowBatteryThreshold: data.lowBatteryThreshold,
        startupMode: data.startupMode,
        systemPrompt: data.systemPrompt,
        voice: voiceStr,
        guardrails: data.guardrails,
        voicePrompt: buildVoicePromptPayload({
            greeting: data.greeting,
            prompt60s: data.prompt60s,
            prompt120s: data.prompt120s,
            preCallPrompt: data.preCallPrompt,
            endPrompt: data.endPrompt,
        }),
        kbIds: newkbIds,
        skillIds: data.skillIds,
        activate: data.activate,
        timeout: data.timeout,
    };

    Object.keys(payload).forEach(key => {
        if (payload[key] === undefined) {
            delete payload[key];
        }
    });

    try {
        const response = await api.put(
            `/v1/robot/config/${id}`,
            payload,
        );
        return response.data
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi cập nhật cấu hình robot.';
        throw new Error(message);
    }
}

/**
 * Activate robot by ID
 */
export async function activateRobot(
    id: string,
    payload: ActivateRobotPayload,
    signal?: AbortSignal,
): Promise<any> {
    const response = await api.post(`/v1/robot/config/${id}/activate`, payload, {
        signal,
    })
    return response.data;
}

export async function retrySendMap(
    id: string,
    signal?: AbortSignal,
): Promise<any> {
    const response = await api.post(`/v1/robot/send-map/${id}`, {
        signal,
    })
    return response;
}

export async function moveRobot(
    id: string,
    payload: { x: number; y: number; yaw: number },
    signal?: AbortSignal,
): Promise<any> {
    const response = await api.post(`/v1/robot/move-to/${id}`, payload, {
        signal,
    })
    return response.data;
}

export async function goHome(
    id: string,
    signal?: AbortSignal,
): Promise<any> {
    const response = await api.post(`/v1/robot/go-home/${id}`, {
        signal,
    })
    return response.data;
}

export async function deleteRobot(robotId: string): Promise<any> {
    try {
        const response = await api.delete(`/v1/robot/config/${robotId}`)
        return response.data
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi xóa robot.';
        throw new Error(message);
    }
}
